/* eslint-disable jira/filename-case, jira/jira-ssr/no-unchecked-globals-usage, @atlaskit/platform/no-direct-document-usage */
/* oxlint-disable no-console */
import { captureDocumentState } from '@jira/platform__hydration-preservation-tracker/src/preservationTracker.tsx';
import { getIsHydrationInProgress } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { callWhenIdle } from '@jira/platform__utils-call-when-idle';
import { getPlaceholderMountTracker } from '@atlassian/jira-placeholder/src/useMountTracker.tsx';

import { getReactFiber, createFiberDebugObject } from './react-fiber.tsx';

const LOG_PREFIX = 'HYDRATION_DEBUGGER: ';

const highlight = (
	overlayContainer: HTMLElement,
	target: HTMLElement,
	colour: string,
	printReport: () => void,
) => {
	if (overlayContainer.contains(target)) {
		// Avoid infinite loop
		return;
	}

	const { display } = window.getComputedStyle(target);
	let visibleTarget: HTMLElement | undefined;
	if (display === 'contents') {
		const firstChild = [...target.children][0];
		if (firstChild instanceof HTMLElement) {
			visibleTarget = firstChild;
		} else {
			visibleTarget = undefined;
		}
	} else {
		visibleTarget = target;
	}
	if (!visibleTarget) {
		return;
	}

	const highlightElement = document.createElement('div');
	highlightElement.classList.add('hydration-overlay-target', colour);
	const { top, left, width, height } = visibleTarget.getBoundingClientRect();
	const { scrollY, scrollX } = window;
	Object.assign(highlightElement.style, {
		top: `${scrollY + top}px`,
		left: `${scrollX + left}px`,
		width: `${width}px`,
		height: `${height}px`,
		border: `1px solid ${colour}`,
	});
	highlightElement.addEventListener('click', () => {
		printReport();
		highlightElement.remove();
	});
	overlayContainer.appendChild(highlightElement);
};

type ElementPredicate = (element: HTMLElement) => boolean;
const addElementIgnores: ElementPredicate[] = [
	(element) => element.dataset.testid === 'page-layout.root--skip-links-container',
	(element) =>
		document
			.querySelector('[data-testid="page-layout.root--skip-links-container"]')
			?.contains(element) ?? false,
	(element) => element.tagName === 'STYLE',
];

const removeElementIgnores: ElementPredicate[] = [
	(element) => element.nodeType === Node.COMMENT_NODE,
	(element) => element.tagName === 'TEMPLATE',
];

const createOverlayContainer = () => {
	const overlayContainer = document.createElement('div');
	overlayContainer.setAttribute('id', 'hydration-debugger-overlay');
	const overlayStyles = document.createElement('style');
	overlayStyles.innerHTML = `
		.hydration-overlay-target {
			position: absolute;
			z-index: 1000;
			background-color: rgba(0, 0, 0, 0.1);
			cursor: crosshair;
		}
		.hydration-overlay-target.red:hover {
			background-color: red;
		}
		.hydration-overlay-target.orange:hover {
			background-color: orange;
		}
	`;
	overlayContainer.appendChild(overlayStyles);
	return overlayContainer;
};

const runOnHydrationCompletion = (onCompleted: () => void) => {
	if (getIsHydrationInProgress()) {
		callWhenIdle(() => runOnHydrationCompletion(onCompleted));
	} else {
		onCompleted();
	}
};

type ReportedHydrationError = [string, any];
declare global {
	interface Window {
		__HYDRATION_ERRORS__?: ReportedHydrationError[];
		__HYDRATION_RECOVERABLE_ERRORS__?: unknown[];
	}
}

const IGNORE_CHANGED_ATTRIBUTE_NAMES = ['data-ssr-placeholder', 'data-ssr-placeholder-replace'];

const highlightChangesReportedByReact = (overlayContainer: HTMLElement) => {
	if (!window.__HYDRATION_ERRORS__) {
		return;
	}

	for (const err of window.__HYDRATION_ERRORS__) {
		const [errorType, errorDetails] = err;
		const target = errorDetails.domElement;
		const debugArgs: unknown[] = [];
		if (target) {
			debugArgs.push(target);
			const fiber = getReactFiber(target);
			const parentFiber = target.parentElement && getReactFiber(target.parentElement);
			if (fiber) {
				debugArgs.push('\n\t--> fiber:', fiber, 'debug info:', createFiberDebugObject(fiber));
			} else if (parentFiber) {
				debugArgs.push(
					'\n\t--> parent fiber:',
					parentFiber,
					'debug info:',
					createFiberDebugObject(parentFiber),
				);
			}
		}

		const report = (message: string, ...args: unknown[]) => {
			console.log(`${LOG_PREFIX}${message}`, ...args);
		};
		const reportAttributeChange = (printReport: () => void) => {
			printReport();
			highlight(overlayContainer, target, 'orange', printReport);
		};
		switch (errorType) {
			case 'warnForPropDifference': {
				const { serverValue, clientValue, propKey } = errorDetails;
				if (!IGNORE_CHANGED_ATTRIBUTE_NAMES.includes(propKey)) {
					reportAttributeChange(() =>
						report(
							`attribute "${propKey}" changed from serverValue=${JSON.stringify(serverValue)} to clientValue=${JSON.stringify(
								clientValue,
							)} on element: `,
							...debugArgs,
						),
					);
				}
				break;
			}
			case 'warnForExtraAttributes': {
				for (const attributeName of errorDetails.attributeNames) {
					if (!IGNORE_CHANGED_ATTRIBUTE_NAMES.includes(attributeName)) {
						reportAttributeChange(() =>
							report(`attribute "${attributeName}" added to element: `, ...debugArgs),
						);
					}
				}
				break;
			}
			default:
				report(errorType, errorDetails);
				// oxlint-disable-next-line no-continue
				continue;
		}
	}
};

export const installSPADebuggingTools = (): void => {
	// Expose for testing if debugger is active
	// @ts-expect-error TS2339: Property &#39;getPlaceholderMountTracker&#39; does not exist on type &#39;Window
	window.getPlaceholderMountTracker = getPlaceholderMountTracker;
};

export const installHydrationDebugger = (rootContainer: HTMLElement): void => {
	const overlayContainer = createOverlayContainer();
	document.querySelector('body')?.appendChild(overlayContainer);

	const collectChangesAfterHydrationComplete = captureDocumentState(rootContainer);
	const ssrHtml = rootContainer.outerHTML;
	runOnHydrationCompletion(() => {
		const hydrationOutcomeStats = collectChangesAfterHydrationComplete();
		const outcomeFromErrors = [];
		if (window.__HYDRATION_RECOVERABLE_ERRORS__) {
			outcomeFromErrors.push(
				'\nHydration failure, errors reported to hydrateRoot#onRecoverableError:\n',
				window.__HYDRATION_RECOVERABLE_ERRORS__,
			);
		} else {
			outcomeFromErrors.push('\nHydration success\n');
		}
		console.log(
			'====================================================================\n',
			`${LOG_PREFIX}\nOverall hydration stats:\n`,
			hydrationOutcomeStats,
			...outcomeFromErrors,
			'\nInitial SSR HTML:\n',
			{
				get printSsrHtmlToConsole() {
					console.log(LOG_PREFIX, ssrHtml);
					return 'SSR HTML printed to console';
				},
			},
			'\n====================================================================',
		);

		highlightChangesReportedByReact(overlayContainer);
	});

	let reportCount = 0;
	const callback: MutationCallback = (mutations, observer) => {
		if (reportCount > 100) {
			observer.disconnect();
			console.error(`${LOG_PREFIX}too many mutations, MutationObserver disconnected`);
			return;
		}

		// Unregister ourselves once hydration has completed across the whole page
		if (!getIsHydrationInProgress()) {
			// Disconnect and let this execution finish
			observer.disconnect();
		}

		const report = (message: string, ...args: unknown[]) => {
			console.log(`${LOG_PREFIX}${message}`, ...args);
			reportCount += 1;
		};
		for (const mutation of mutations) {
			if (mutation.type === 'childList') {
				for (const added of mutation.addedNodes) {
					if (
						added instanceof HTMLElement &&
						addElementIgnores.every((predicate) => !predicate(added))
					) {
						report('element:', added, 'was added to parent:', mutation.target);
					}
				}
				for (const removed of mutation.removedNodes) {
					const { target } = mutation;
					if (
						removed instanceof HTMLElement &&
						target instanceof HTMLElement &&
						removeElementIgnores.every((predicate) => !predicate(removed))
					) {
						const fiberDebugArgs: unknown[] = [];
						const fiber = getReactFiber(target);
						if (fiber) {
							fiberDebugArgs.push(
								'\n\t--> removed at parent fiber:',
								fiber,
								'debug info:',
								createFiberDebugObject(fiber),
							);
						}
						const printReport = () =>
							report('element:', removed, 'was removed from parent:', target, ...fiberDebugArgs);
						printReport();

						highlight(overlayContainer, target, 'red', printReport);
					}
				}
			}
		}
	};
	const observer = new MutationObserver(callback);
	observer.observe(rootContainer, { subtree: true, childList: true /* attributes: true */ });
};
